/**
 * Distinguishes a network-layer fetch failure — the browser never received a
 * response (request blocked, offline, CORS, or an extension/proxy/firewall) —
 * from an application/HTTP error, where a response came back and was surfaced
 * as an Error. A network failure is what the browser raises as
 * `TypeError: Failed to fetch`.
 */

export type FetchErrorKind = 'network' | 'app'

// The browser's actual fetch network-failure messages, matched exactly so we
// don't mistake an application error that merely contains "failed to fetch"
// (e.g. "Failed to fetch uploaded files: 500") for a network failure.
const NETWORK_ERROR_PATTERN =
  /^(failed to fetch|load failed|network request failed)$|networkerror when attempting to fetch/i

export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true
  if (error instanceof Error) return NETWORK_ERROR_PATTERN.test(error.message)
  return false
}

export function classifyFetchError(error: unknown): FetchErrorKind {
  return isNetworkError(error) ? 'network' : 'app'
}

export const NETWORK_ERROR_MESSAGE =
  "Couldn't reach the server. A browser extension, ad blocker, or your network or firewall may be blocking the request. Try again, disable extensions, or switch networks."

// Shown when the upload failed at the network layer but a probe confirms the
// server is reachable — i.e. the upload itself was blocked before it was sent.
export const UPLOAD_BLOCKED_MESSAGE =
  "We reached the server, but your file upload didn't go through. A browser extension, ad blocker, or your network or firewall may be blocking file uploads. Try another network or browser, or disable extensions, then retry."

// Shown when even a known-good request can't reach the server.
export const SERVER_UNREACHABLE_MESSAGE =
  "Couldn't reach the server. Check your internet connection and try again."
